/**
 * DataToolPanel - Unified panel for data manipulation tools
 * Story 4.5: Data Tool Panel UI
 * AC-4.5.1: Tabbed interface with Filter, Sort, Group, Transpose tabs
 * AC-4.5.2: Each tab shows current configuration with remove buttons
 * AC-4.5.3: Changes apply immediately (live preview)
 * AC-4.5.4: Reset button clears all settings
 * AC-4.5.6: Panel is resizable
 * AC-4.5.7: Panel is dockable (right or bottom)
 * AC-4.5.8: Panel is dismissible (click outside or Escape)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo, forwardRef } from 'react';
import { X, RotateCcw, PanelRightClose, PanelBottomClose, GripVertical } from 'lucide-react';
import type {
  GridColumn,
  DateRangeFilter,
  SortConfig,
} from '../types/gridTypes';
import type { GroupingConfig } from '../../../types/types/view';
import { DateRangeFilterPanel } from './DateRangeFilterPanel';
import { GroupingPanel } from './GroupingPanel';
import { SortConfigPanel } from './SortConfigPanel';

// =============================================================================
// TYPES
// =============================================================================

export type DockPosition = 'right' | 'bottom';
export type DataToolTab = 'filter' | 'sort' | 'group';

const STORAGE_KEY = 'data-tool-panel-dock';
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const MIN_HEIGHT = 300;
const MAX_HEIGHT_RATIO = 0.8; // 80vh

export interface DataToolPanelProps {
  columns: GridColumn[];
  // Filter config
  dateRangeFilter: DateRangeFilter | null;
  onDateRangeFilterChange: (filter: DateRangeFilter | null) => void;
  // Sort config
  sortConfig: SortConfig[] | null;
  onSortConfigChange: (config: SortConfig[] | null) => void;
  // Grouping config
  groupingConfig: GroupingConfig | null;
  onGroupingConfigChange: (config: GroupingConfig) => void;
  // Panel state
  onClose: () => void;
  // Optional initial tab
  initialTab?: DataToolTab;
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface TabButtonProps {
  tab: DataToolTab;
  activeTab: DataToolTab;
  label: string;
  badgeCount: number;
  onClick: (tab: DataToolTab) => void;
}

const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  function TabButton({ tab, activeTab, label, badgeCount, onClick }, ref) {
    const isActive = tab === activeTab;

    return (
      <button
        ref={ref}
        role="tab"
        aria-selected={isActive}
        aria-controls={`tabpanel-${tab}`}
        id={`tab-${tab}`}
        onClick={() => onClick(tab)}
        className={`
          relative px-4 py-2 text-sm font-medium transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
          ${isActive
            ? 'text-blue-700 dark:text-blue-300 border-b-2 border-blue-500'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 border-b-2 border-transparent'
          }
        `}
      >
        {label}
        {badgeCount > 0 && (
          <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
            {badgeCount}
          </span>
        )}
      </button>
    );
  }
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DataToolPanel({
  columns,
  dateRangeFilter,
  onDateRangeFilterChange,
  sortConfig,
  onSortConfigChange,
  groupingConfig,
  onGroupingConfigChange,
  onClose,
  initialTab = 'filter',
}: DataToolPanelProps) {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<DataToolTab>(initialTab);
  const [dockPosition, setDockPosition] = useState<DockPosition>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'right' || saved === 'bottom') return saved;
    }
    return 'right';
  });
  const [panelWidth, setPanelWidth] = useState(380);
  const [panelHeight, setPanelHeight] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // ---------------------------------------------------------------------------
  // BADGE COUNTS
  // ---------------------------------------------------------------------------

  const filterCount = dateRangeFilter ? 1 : 0;
  const sortCount = sortConfig?.length || 0;
  const groupCount = groupingConfig?.enabled ? groupingConfig.fields.length : 0;
  const totalActiveConfigs = filterCount + sortCount + groupCount;

  // ---------------------------------------------------------------------------
  // DOCK POSITION PERSISTENCE
  // ---------------------------------------------------------------------------

  const handleDockPositionChange = useCallback((position: DockPosition) => {
    setDockPosition(position);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, position);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // RESET ALL (AC-4.5.4)
  // ---------------------------------------------------------------------------

  const handleResetAll = useCallback(() => {
    // Confirm if any config is active
    if (totalActiveConfigs > 0) {
      const confirmed = window.confirm(
        `Reset all Data Tool settings? This will clear ${totalActiveConfigs} active configuration(s).`
      );
      if (!confirmed) return;
    }

    onDateRangeFilterChange(null);
    onSortConfigChange(null);
    onGroupingConfigChange({ enabled: false, fields: [], hideEmptyGroups: false, defaultExpanded: true, expandedGroupIds: [] });
  }, [
    totalActiveConfigs,
    onDateRangeFilterChange,
    onSortConfigChange,
    onGroupingConfigChange,
  ]);

  // ---------------------------------------------------------------------------
  // KEYBOARD NAVIGATION (AC-4.5.8)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }

      // Arrow key navigation when tab list is focused
      if (document.activeElement?.getAttribute('role') === 'tab') {
        const tabs: DataToolTab[] = ['filter', 'sort', 'group'];
        const currentIndex = tabs.indexOf(activeTab);

        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextIndex = (currentIndex + 1) % tabs.length;
          setActiveTab(tabs[nextIndex]);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          setActiveTab(tabs[prevIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, onClose]);

  // ---------------------------------------------------------------------------
  // FOCUS MANAGEMENT (AC-4.5.8)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Store previous focus
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus first tab button on mount
    firstFocusableRef.current?.focus();

    // Restore focus on unmount
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  // ---------------------------------------------------------------------------
  // RESIZE HANDLING (AC-4.5.6)
  // ---------------------------------------------------------------------------

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = panelWidth;
    const startHeight = panelHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (dockPosition === 'right') {
        const deltaX = startX - moveEvent.clientX;
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + deltaX));
        setPanelWidth(newWidth);
      } else {
        const deltaY = startY - moveEvent.clientY;
        const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
        const newHeight = Math.min(maxHeight, Math.max(MIN_HEIGHT, startHeight + deltaY));
        setPanelHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [dockPosition, panelWidth, panelHeight]);

  // ---------------------------------------------------------------------------
  // PANEL STYLES
  // ---------------------------------------------------------------------------

  const panelStyles = useMemo(() => {
    const baseStyles = {
      backgroundColor: 'var(--surface-elevated, #ffffff)',
      borderColor: 'var(--border-default, #e2e8f0)',
      boxShadow: 'var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1))',
    };

    if (dockPosition === 'right') {
      return {
        ...baseStyles,
        position: 'fixed' as const,
        top: 0,
        right: 0,
        width: panelWidth,
        height: '100vh',
        zIndex: 20,
      };
    } else {
      return {
        ...baseStyles,
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        height: panelHeight,
        zIndex: 20,
      };
    }
  }, [dockPosition, panelWidth, panelHeight]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={panelRef}
      style={panelStyles}
      className="flex flex-col border-l dark:border-gray-700 bg-white dark:bg-gray-900"
      role="dialog"
      aria-modal="true"
      aria-label="Data Tool Panel"
    >
      {/* Resize Handle */}
      <div
        className={`
          absolute cursor-${dockPosition === 'right' ? 'ew' : 'ns'}-resize
          ${dockPosition === 'right'
            ? 'left-0 top-0 bottom-0 w-1 hover:bg-blue-400'
            : 'top-0 left-0 right-0 h-1 hover:bg-blue-400'
          }
          ${isResizing ? 'bg-blue-500' : 'bg-transparent'}
          transition-colors
        `}
        onMouseDown={handleResizeStart}
      >
        <div className={`
          absolute flex items-center justify-center
          ${dockPosition === 'right'
            ? 'left-0 top-1/2 -translate-y-1/2 w-3 h-8 -ml-1'
            : 'top-0 left-1/2 -translate-x-1/2 h-3 w-8 -mt-1'
          }
          bg-gray-200 dark:bg-gray-700 rounded
        `}>
          <GripVertical className={`w-3 h-3 text-gray-400 ${dockPosition === 'bottom' ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Data Tool
          {totalActiveConfigs > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
              {totalActiveConfigs} active
            </span>
          )}
        </h2>

        <div className="flex items-center gap-2">
          {/* Dock position toggle */}
          <button
            onClick={() => handleDockPositionChange(dockPosition === 'right' ? 'bottom' : 'right')}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded transition-colors"
            title={`Dock ${dockPosition === 'right' ? 'bottom' : 'right'}`}
          >
            {dockPosition === 'right' ? (
              <PanelBottomClose className="w-4 h-4" />
            ) : (
              <PanelRightClose className="w-4 h-4" />
            )}
          </button>

          {/* Reset button */}
          <button
            onClick={handleResetAll}
            disabled={totalActiveConfigs === 0}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Reset all settings"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded transition-colors"
            title="Close (Escape)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tab List */}
      <div
        role="tablist"
        aria-label="Data Tool tabs"
        className="flex border-b dark:border-gray-700 px-2"
      >
        <TabButton
          ref={firstFocusableRef}
          tab="filter"
          activeTab={activeTab}
          label="Filter"
          badgeCount={filterCount}
          onClick={setActiveTab}
        />
        <TabButton
          tab="sort"
          activeTab={activeTab}
          label="Sort"
          badgeCount={sortCount}
          onClick={setActiveTab}
        />
        <TabButton
          tab="group"
          activeTab={activeTab}
          label="Group"
          badgeCount={groupCount}
          onClick={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'filter' && (
          <div
            id="tabpanel-filter"
            role="tabpanel"
            aria-labelledby="tab-filter"
            className="p-0"
          >
            <DateRangeFilterPanel
              columns={columns}
              filter={dateRangeFilter}
              onFilterChange={onDateRangeFilterChange}
            />
          </div>
        )}

        {activeTab === 'sort' && (
          <div
            id="tabpanel-sort"
            role="tabpanel"
            aria-labelledby="tab-sort"
            className="p-0"
          >
            <SortConfigPanel
              columns={columns}
              sortConfig={sortConfig}
              onSortConfigChange={onSortConfigChange}
            />
          </div>
        )}

        {activeTab === 'group' && (
          <div
            id="tabpanel-group"
            role="tabpanel"
            aria-labelledby="tab-group"
            className="p-0"
          >
            <GroupingPanel
              columns={columns}
              config={groupingConfig}
              onConfigChange={onGroupingConfigChange}
            />
          </div>
        )}
      </div>

      {/* Footer with Save as View (deferred to Story 4.6) */}
      <div className="px-4 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <button
          disabled
          className="w-full px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-200 dark:bg-gray-700 rounded cursor-not-allowed"
          title="Save as View (coming in Story 4.6)"
        >
          Save as View
        </button>
      </div>
    </div>
  );
}

export default DataToolPanel;
